import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useSensorData } from '@/hooks/useSensorData';
import { useWaterTestData } from '@/hooks/useWaterTestData';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { measurePerformance } from '@/utils/performance';
import { calculateTargetRangeAxis } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useEffect, useMemo, useRef } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { DEVICE_COLORS, CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import type { ChartAnnotation } from '@/types/chartAnnotation';

interface PhChartProps {
  startDate: Date;
  endDate: Date;
  /** Pre-fetched Apex sensor data (pH, ph2) */
  sensorData?: Array<{ timestamp: string; pH: number | null; ph2?: number | null }>;
  /** Pre-fetched AquaWiz pH water test data */
  aquawizData?: Array<{ timestamp: string; value: number; device: string; note?: string }>;
  /** Pre-fetched manual pH water test data */
  manualData?: Array<{ timestamp: string; value: number; device: string; note?: string }>;
  /** Pre-fetched Alkatronic pH water test data */
  alkatronicData?: Array<{ timestamp: string; value: number; device: string; note?: string }>;
  /** Pre-fetched Kamoer KH Carer pH water test data */
  kamoerData?: Array<{ timestamp: string; value: number; device: string; note?: string }>;
  /** Pre-fetched target ranges for axis scaling */
  targetRangesData?: Array<{ parameterId: string; minValue: number | null; maxValue: number | null; unit: string }>;
  /** True when moving average is applied - sensor data uses timestamps directly */
  isAveraged?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
  /** Annotations to display on the chart */
  annotations?: ChartAnnotation[];
  /** Callback when the "Add note" button is clicked */
  onAddNote?: () => void;
  /** Callback when an annotation is clicked for editing */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

function roundToNearest10Minutes(timestamp: string | number): number {
  const date = new Date(timestamp);

  // Validate date
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp: ${timestamp}`);
  }

  const minutes = date.getUTCMinutes();
  const roundedMinutes = Math.round(minutes / 10) * 10;

  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    roundedMinutes,
    0,
    0
  );
}

interface ChartDataPoint {
  timestamp: number;
  apex?: number;
  apex_ph2?: number;  // Secondary pH probe
  aquawiz?: number;
  alkatronic?: number;
  kamoer?: number;
  manual?: number;
  [key: `${string}_note`]: string | undefined;
}

export function PhChart({
  startDate,
  endDate,
  sensorData,
  aquawizData: parentAquawizData,
  manualData: parentManualData,
  alkatronicData: parentAlkatronicData,
  kamoerData: parentKamoerData,
  targetRangesData,
  isAveraged = false,
  isLoading: parentLoading,
  error: parentError,
  expanded,
  onToggle,
  annotations,
  onAddNote,
  onAnnotationClick,
}: PhChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Responsive hooks
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Use hooks as fallback when parent doesn't provide data
  // Disable the hook queries when parent provides data OR parent is loading
  const sensorHook = useSensorData({
    startDate,
    endDate,
    parameters: ['pH', 'ph2'],  // Include pH2
    enabled: sensorData === undefined && parentLoading !== true,
  });

  const aquawizHook = useWaterTestData({
    startDate,
    endDate,
    paramName: 'pH_aquawiz',
    device: 'aquawiz',
    enabled: parentAquawizData === undefined && parentLoading !== true,
  });

  // Use parent data if provided, otherwise fall back to hook data
  const rawSensorData = sensorData ?? sensorHook.data?.data;
  const rawAquawizData = parentAquawizData ?? aquawizHook.data?.data;
  const rawManualData = parentManualData;
  const rawAlkatronicData = parentAlkatronicData;
  const rawKamoerData = parentKamoerData;
  const isLoading = parentLoading ?? (sensorHook.isLoading || aquawizHook.isLoading);
  const error = parentError ?? sensorHook.error ?? aquawizHook.error;

  // Group data by timestamp
  // When isAveraged=true (moving average applied), use sensor timestamps directly
  // (already aggregated server-side to 4-hour intervals)
  // AquaWiz and Manual always use rounding (discrete measurements, not averaged)
  const chartData = useMemo(() => {
    if (!rawSensorData && !rawAquawizData && !rawManualData && !rawAlkatronicData && !rawKamoerData) return [];

    const grouped = new Map<number, ChartDataPoint>();

    // Add Apex pH data
    // For averaged data, use timestamps directly (already aggregated)
    // For raw data, round to 10 minutes for grouping
    if (rawSensorData) {
      rawSensorData.forEach((point) => {
        const timestamp = isAveraged
          ? new Date(point.timestamp).getTime()
          : roundToNearest10Minutes(point.timestamp);
        if (!grouped.has(timestamp)) {
          grouped.set(timestamp, { timestamp });
        }
        const entry = grouped.get(timestamp);
        if (entry) {
          // Add pH if not null
          if (point.pH != null) {
            entry.apex = point.pH;
          }
          // Add pH2 if not null
          if (point.ph2 != null) {
            entry.apex_ph2 = point.ph2;
          }
        }
      });
    }

    // Add AquaWiz pH data - always use rounding (discrete measurements)
    if (rawAquawizData) {
      rawAquawizData.forEach((point) => {
        const roundedTimestamp = roundToNearest10Minutes(point.timestamp);
        if (!grouped.has(roundedTimestamp)) {
          grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
        }
        const entry = grouped.get(roundedTimestamp);
        if (entry) {
          entry.aquawiz = point.value;
          if (point.note) entry.aquawiz_note = point.note;
        }
      });
    }

    // Add Manual pH data - always use rounding (discrete measurements)
    if (rawManualData) {
      rawManualData.forEach((point) => {
        const roundedTimestamp = roundToNearest10Minutes(point.timestamp);
        if (!grouped.has(roundedTimestamp)) {
          grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
        }
        const entry = grouped.get(roundedTimestamp);
        if (entry) {
          entry.manual = point.value;
          if (point.note) entry.manual_note = point.note;
        }
      });
    }

    // Add Alkatronic pH data - always use rounding (discrete measurements)
    if (rawAlkatronicData) {
      rawAlkatronicData.forEach((point) => {
        const roundedTimestamp = roundToNearest10Minutes(point.timestamp);
        if (!grouped.has(roundedTimestamp)) {
          grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
        }
        const entry = grouped.get(roundedTimestamp);
        if (entry) {
          entry.alkatronic = point.value;
          if (point.note) entry.alkatronic_note = point.note;
        }
      });
    }

    // Add Kamoer pH data - always use rounding (discrete measurements)
    if (rawKamoerData) {
      rawKamoerData.forEach((point) => {
        const roundedTimestamp = roundToNearest10Minutes(point.timestamp);
        if (!grouped.has(roundedTimestamp)) {
          grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
        }
        const entry = grouped.get(roundedTimestamp);
        if (entry) {
          entry.kamoer = point.value;
          if (point.note) entry.kamoer_note = point.note;
        }
      });
    }

    return Array.from(grouped.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [rawSensorData, rawAquawizData, rawManualData, rawAlkatronicData, rawKamoerData, isAveraged]);

  // Range-aware X-axis: dates for multi-day, times for single-day
  const timeAxis = useTimeAxis(startDate, endDate);

  // Find pH target range from user config or global defaults
  const phRange = useMemo(() => {
    return targetRangesData?.find(r => r.parameterId === 'ph');
  }, [targetRangesData]);

  // Calculate Y-axis from target ranges + data (no hardcoded axis bounds)
  const yAxisConfig = useMemo(() => {
    const values: number[] = [];
    chartData.forEach((d) => {
      if (d.apex != null) values.push(d.apex);
      if (d.apex_ph2 != null) values.push(d.apex_ph2);
      if (d.aquawiz != null) values.push(d.aquawiz);
      if (d.manual != null) values.push(d.manual);
      if (d.alkatronic != null) values.push(d.alkatronic);
      if (d.kamoer != null) values.push(d.kamoer);
    });

    const hasValues = values.length > 0;

    return calculateTargetRangeAxis({
      dataMin: hasValues ? Math.min(...values) : undefined,
      dataMax: hasValues ? Math.max(...values) : undefined,
      targetMin: phRange?.minValue ?? undefined,
      targetMax: phRange?.maxValue ?? undefined,
    });
  }, [chartData, phRange]);

  // Measure chart render performance
  useEffect(() => {
    if (chartData && !isLoading) {
      const measure = measurePerformance('PhChart');
      measure.end();
    }
  }, [chartData, isLoading]);

  if (isLoading) {
    return <ChartSkeleton title="pH" />;
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    return (
      <Alert color="red" title="Error loading pH data">
        {errorMessage}
      </Alert>
    );
  }

  if (chartData.length === 0 || yAxisConfig === null) {
    return (
      <Alert color="blue" title="No data available">
        No pH data found for the selected date range.
      </Alert>
    );
  }

  // Detect which data sources have data for conditional legend rendering
  const hasApex = chartData.some((d) => d.apex !== undefined);
  const hasApexPh2 = chartData.some((d) => d.apex_ph2 !== undefined);
  const hasAquawiz = chartData.some((d) => d.aquawiz !== undefined);
  const hasManual = chartData.some((d) => d.manual !== undefined);
  const hasAlkatronic = chartData.some((d) => d.alkatronic !== undefined);
  const hasKamoer = chartData.some((d) => d.kamoer !== undefined);

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="pH"
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
        onAddNote={onAddNote}
      />

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          {/* Annotation regions - rendered behind data lines */}
          {annotations && (
            <ChartAnnotationLayer
              annotations={filterAnnotationsForChart(
                annotations,
                'ph',
                ['apex', 'aquawiz', 'manual', 'kamoer_kh']
              )}
              onAnnotationClick={onAnnotationClick}
            />
          )}

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          {/* Crosshair tooltip with custom content */}
          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => v.toFixed(3)}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {/* Apex pH line - only show if has data */}
          {hasApex && (
            <Line
              type="linear"
              dataKey="apex"
              stroke={DEVICE_COLORS.apex}
              strokeWidth={1.5}
              dot={false}
              name="Apex pH"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}

          {/* Apex pH2 line - only show if has data */}
          {hasApexPh2 && (
            <Line
              type="monotone"
              dataKey="apex_ph2"
              stroke={DEVICE_COLORS.apex2}
              strokeWidth={2}
              dot={false}
              name="Apex pH2"
              activeDot={{ r: 6 }}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* AquaWiz pH line - only show if has data */}
          {hasAquawiz && (
            <Line
              type="monotone"
              dataKey="aquawiz"
              stroke={DEVICE_COLORS.aquawiz}
              strokeWidth={2}
              dot={{ r: 3, fill: DEVICE_COLORS.aquawiz }}
              name="AquaWiz"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}

          {/* Alkatronic pH line - only show if has data */}
          {hasAlkatronic && (
            <Line
              type="monotone"
              dataKey="alkatronic"
              stroke={DEVICE_COLORS.alkatronic}
              strokeWidth={2}
              dot={{ r: 3, fill: DEVICE_COLORS.alkatronic }}
              name="Alkatronic"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}

          {/* Kamoer KH Carer pH line - only show if has data */}
          {hasKamoer && (
            <Line
              type="monotone"
              dataKey="kamoer"
              stroke={DEVICE_COLORS.kamoer_kh}
              strokeWidth={2}
              dot={{ r: 3, fill: DEVICE_COLORS.kamoer_kh }}
              name="Kamoer KH"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}

          {/* Manual pH entries - only show if has data */}
          {hasManual && (
            <Line
              type="monotone"
              dataKey="manual"
              stroke={DEVICE_COLORS.manual}
              strokeWidth={1.5}
              dot={{ r: 4, fill: DEVICE_COLORS.manual, strokeWidth: 0 }}
              name="Manual"
              activeDot={{ r: 6 }}
              connectNulls={true}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}
